import Tabs from "@/v2-backoffice-client/components/tabs"
import FeatureAvailabilityTab from "@/v2-backoffice-client/feature/availability/FeatureAvailabilityTab"
import FeatureConfigurationTab from "@/v2-backoffice-client/feature/configuration/FeatureConfigurationTab"
import FeatureLiveEditNotice from "@/v2-backoffice-client/feature/FeatureLiveEditNotice"
import { FEATURE_TAB } from "@/v2-backoffice-client/feature/feature.constants"
import TranslationAttributesTab from "@/v2-backoffice-client/translation/attributes/TranslationAttributesTab"
import TranslationEditor from "@/v2-backoffice-client/translation/editor/TranslationEditor"
import { FEATURE_ROW_TABS_ID } from "@/v2-backoffice-client/translation/translation.constants"
import type { SavedTranslationText } from "@/v2-backoffice-client/translation/editor/translation.editor.utils"
import type {
  SupportedLanguage,
  TranslationRow,
} from "@/v2-backoffice-client/translation/translation.types"

export type TranslationCatalogRowDetailProps = {
  row: TranslationRow
  languages: SupportedLanguage[]
  /**
   * Every value of the row's type set, for the attributes shape hint. Type
   * values only; a feature's row detail never reads it.
   */
  siblingRows: TranslationRow[]
  // Which facet to open on. The publish review passes the one that changed,
  // so a configuration diff lands on the configuration editor; the catalog
  // leaves it at Text.
  defaultTab?: (typeof FEATURE_TAB)[keyof typeof FEATURE_TAB]
  onSaved(): void
  onSavedTexts(texts: SavedTranslationText[]): void
  onDeleted(): void
  // A rename retires this row's identity exactly like a delete does, so
  // owners treat both the same way: collapse the row and refetch.
  onRenamed?(): void
}

/**
 * The three facets of one catalog entry, one per question an operator is
 * answering: what does it say, how does it behave, who gets it.
 *
 * Text and the other two save differently, and the tabs say so: text is
 * authored in the catalog and ships with the next publish, while
 * configuration and availability write the running environment immediately.
 * That is the same split the layer and basemap sheets already have.
 *
 * A TYPE VALUE (a data label, e.g. an ownership type) is not a feature: it has
 * no countries and no availability, so instead of a tab strip that is half
 * inapplicable it gets the two facets it does have — its text, and the extra
 * columns its type table carries (Attributes), which stand in for a feature's
 * Configuration and are what the runtime seed reads.
 */
function TranslationCatalogRowDetail(props: TranslationCatalogRowDetailProps) {
  const {
    row,
    languages,
    siblingRows,
    defaultTab = FEATURE_TAB.text,
    onSaved,
    onSavedTexts,
    onDeleted,
    onRenamed,
  } = props

  const editor = (
    <TranslationEditor
      row={row}
      languages={languages}
      onSaved={onSaved}
      onSavedTexts={onSavedTexts}
      onDeleted={onDeleted}
      onRenamed={onRenamed}
    />
  )

  if (row.objectType !== "Feature") {
    return (
      <Tabs id={FEATURE_ROW_TABS_ID} defaultValue={defaultTab}>
        <Tabs.List>
          <Tabs.Trigger value={FEATURE_TAB.text}>Text</Tabs.Trigger>
          <Tabs.Trigger value={FEATURE_TAB.attributes}>Attributes</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value={FEATURE_TAB.text}>{editor}</Tabs.Content>

        <Tabs.Content value={FEATURE_TAB.attributes}>
          <TranslationAttributesTab
            row={row}
            siblingRows={siblingRows}
            onSaved={onSaved}
          />
        </Tabs.Content>
      </Tabs>
    )
  }

  return (
    <Tabs id={FEATURE_ROW_TABS_ID} defaultValue={defaultTab}>
      <Tabs.List>
        <Tabs.Trigger value={FEATURE_TAB.text}>Text</Tabs.Trigger>
        <Tabs.Trigger value={FEATURE_TAB.configuration}>
          Configuration
        </Tabs.Trigger>
        <Tabs.Trigger value={FEATURE_TAB.availability}>
          Availability
        </Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value={FEATURE_TAB.text}>{editor}</Tabs.Content>

      <Tabs.Content value={FEATURE_TAB.configuration}>
        <FeatureLiveEditNotice className="mx-3 mt-3" />
        <FeatureConfigurationTab
          featureCode={row.code}
          featureCodeLabel="Features"
        />
      </Tabs.Content>

      <Tabs.Content value={FEATURE_TAB.availability}>
        <FeatureLiveEditNotice className="mx-3 mt-3" />
        <FeatureAvailabilityTab
          featureCode={row.code}
          subject="feature"
          featureCodeLabel="Features"
        />
      </Tabs.Content>
    </Tabs>
  )
}

export default TranslationCatalogRowDetail
